import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BidV2Status, type BuyerBidV2DetailDto } from "../../../../autogen/bff-api";
import { getBidMinimumRequirementProgress, getBidMinimumRequirementRows } from "./bidMinimumRequirements";
import { MinimumRequirementReviewCard } from "./MinimumRequirementReviewCard";
import { useBuyerMinimumRequirementReviewEditor } from "./useBuyerMinimumRequirementReviewEditor";

interface Props {
  eventId: string;
  bidId: string;
  detail: BuyerBidV2DetailDto;
}

export const MinimumRequirementsTab = ({ eventId, bidId, detail }: Props) => {
  const { t } = useTranslation();
  const editor = useBuyerMinimumRequirementReviewEditor(eventId, bidId, detail);
  const { hasRequirements, fulfilled, notFulfilled, pending, total } = useMemo(
    () => getBidMinimumRequirementProgress(detail),
    [detail],
  );
  const rows = useMemo(() => getBidMinimumRequirementRows(detail), [detail]);
  const isRejected = detail.bid.status === BidV2Status.Rejected;

  if (!hasRequirements) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="2"
        py="16"
        rounded="xl"
        bg="smBackground"
        border="1px dashed"
        borderColor="smBorder"
        color="smMuted"
      >
        <Text fontWeight="medium">{t("No minimum requirements")}</Text>
      </Flex>
    );
  }

  return (
    <Stack spacing="6">
      <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
        <Text color="smMuted" fontSize="sm">
          {pending === 0
            ? fulfilled === total
              ? t("All {{total}} requirements are fulfilled", { total })
              : t("{{count}} of {{total}} fulfilled", { count: fulfilled, total })
            : t("{{count}} of {{total}} reviewed", { count: fulfilled + notFulfilled, total })}
        </Text>
      </Box>

      {rows.map(({ requirement, response }) => {
        const id = requirement.id ?? "";
        return (
          <MinimumRequirementReviewCard
            key={id}
            id={id}
            title={requirement.title}
            description={requirement.description}
            responseType={requirement.responseType}
            supplierAnswer={response?.answer}
            supplierComment={response?.comment ?? null}
            supplierAttachments={response?.attachments ?? null}
            verdict={editor.state.verdicts[id] ?? null}
            comment={editor.state.comments[id] ?? ""}
            onVerdictChange={editor.setVerdict}
            onCommentChange={editor.setComment}
            isReadOnly={isRejected}
          />
        );
      })}
    </Stack>
  );
};
